import React, { useMemo, useState, useEffect } from "react";
import { AddTransactionForm } from "../../components/AddTransaction/AddTransactionForm.tsx";
import { useTransactions } from "../../hooks/useTransactions.tsx";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import "dayjs/locale/en";
import { TransactionFormData, Transaction } from "../../types/transaction.tsx";
import { categories } from "../../data/categories.tsx";
import { transactionTypes } from "../../data/transactionTypes.tsx";
import Modal from "../../components/Modal/index.tsx";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import Button from "../../components/ui/button.tsx";
import Input from "../../components/ui/input.tsx";
import { SimpleSelect } from "../../components/ui/select-shadcn.tsx";
import Label from "../../components/ui/label.tsx";
import { Table, THead, TBody, TR, TH, TD } from "../../components/ui/table.tsx";
import { useSearchParams } from "react-router-dom";
import {
  Pencil,
  Trash2,
  Check,
  X,
  SlidersHorizontal,
  Download,
  Plus,
} from "lucide-react";

ChartJS.register(ArcElement, Tooltip, Legend);

// Set locale
dayjs.locale("en");

// Format date
const formatDate = (dateString: string) => {
  return dayjs(dateString).format("DD MMM YYYY");
};

export default function Transactions() {
  const [showModal, setShowModal] = useState(false);
  const [filterType, setFilterType] = useState<string>("all");
  const [searchText, setSearchText] = useState<string>("");
  const [period, setPeriod] = useState<string>("month"); // 7d | 30d | month | all | range
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRow, setEditRow] = useState<Partial<Transaction>>({});
  const [showFilters, setShowFilters] = useState(false);

  const [sortBy, setSortBy] = useState<
    "date" | "title" | "type" | "category" | "amount" | "currency"
  >("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const FILTERS_STORAGE_KEY = "filters:transactions";
  const [searchParams, setSearchParams] = useSearchParams();

  const { transactions, addTransaction, deleteTransaction, updateTransaction } =
    useTransactions();

  // Initialize filters: URL params take precedence, then localStorage
  useEffect(() => {
    const hasURL = ["s", "t", "c", "p", "f", "to"].some((k) =>
      searchParams.has(k)
    );
    if (hasURL) {
      const s = searchParams.get("s") ?? "";
      const t = searchParams.get("t") ?? "all";
      const c = searchParams.get("c") ?? "all";
      const p = searchParams.get("p") ?? "month";
      const f = searchParams.get("f") ?? "";
      const toq = searchParams.get("to") ?? "";
      setSearchText(s);
      setFilterType(t);
      setSelectedCategory(c);
      setPeriod(p);
      setDateFrom(f);
      setDateTo(toq);
      return;
    }
    try {
      const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (raw) {
        const f = JSON.parse(raw);
        setSearchText(f.searchText ?? "");
        setFilterType(f.filterType ?? "all");
        setSelectedCategory(f.selectedCategory ?? "all");
        setPeriod(f.period ?? "month");
        setDateFrom(f.dateFrom ?? "");
        setDateTo(f.dateTo ?? "");
      }
    } catch {}
  }, [searchParams]);

  // Persist filters
  useEffect(() => {
    try {
      const f = {
        searchText,
        filterType,
        selectedCategory,
        period,
        dateFrom,
        dateTo,
      };
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(f));
    } catch {}
  }, [searchText, filterType, selectedCategory, period, dateFrom, dateTo]);

  // Sync filters to URL (replace to avoid history spam)
  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchText.trim() !== "") params.s = searchText.trim();
    if (filterType !== "all") params.t = filterType;
    if (selectedCategory !== "all") params.c = selectedCategory;
    if (period !== "month") params.p = period;
    if (period === "range") {
      if (dateFrom) params.f = dateFrom;
      if (dateTo) params.to = dateTo;
    }
    setSearchParams(params, { replace: true });
  }, [
    searchText,
    filterType,
    selectedCategory,
    period,
    dateFrom,
    dateTo,
    setSearchParams,
  ]);

  const handleAddTransaction = (data: TransactionFormData) => {
    addTransaction({
      ...data,
      id: uuidv4(),
      status: "completed",
      category: data.category ?? "miscellaneous",
    });
    setShowModal(false);
  };

  const handleError = (error: unknown) => {
    console.error("Error adding transaction:", error);
  };

  const filteredTransactions = useMemo(() => {
    const now = dayjs();
    const presetStart = (() => {
      if (period === "7d") return now.subtract(7, "day");
      if (period === "30d") return now.subtract(30, "day");
      if (period === "month") return now.startOf("month");
      return null; // all or range
    })();

    return transactions
      .filter((tx) => (filterType === "all" ? true : tx.type === filterType))
      .filter((tx) =>
        selectedCategory && selectedCategory !== "all"
          ? tx.category === selectedCategory
          : true
      )
      .filter((tx) => tx.title.toLowerCase().includes(searchText.toLowerCase()))
      .filter((tx) => {
        const dt = dayjs(tx.date);
        if (period === "range") {
          const from = dateFrom ? dayjs(dateFrom).startOf("day") : null;
          const to = dateTo ? dayjs(dateTo).endOf("day") : null;
          if (from && dt.isBefore(from)) return false;
          if (to && dt.isAfter(to)) return false;
          return true;
        }
        return presetStart ? dt.isAfter(presetStart) : true;
      });
  }, [
    transactions,
    filterType,
    searchText,
    period,
    selectedCategory,
    dateFrom,
    dateTo,
  ]);

  const exportCSV = () => {
    const header = [
      "title",
      "date",
      "type",
      "amount",
      "currency",
      "category",
    ].join(",");
    const rows = filteredTransactions.map((tx) =>
      [
        JSON.stringify(tx.title),
        dayjs(tx.date).toISOString(),
        tx.type,
        tx.amount,
        tx.currency,
        tx.category,
      ].join(",")
    );
    const blob = new Blob([header + "\n" + rows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions_${dayjs().format("YYYYMMDD_HHmm")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const startEdit = (tx: Transaction) => {
    setEditingId(tx.id);
    setEditRow({ ...tx });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditRow({});
  };
  const saveEdit = () => {
    if (!editingId) return;
    const payload: Transaction = {
      ...(editRow as Transaction),
      id: editingId,
      amount: Number(editRow.amount || 0),
    };
    // capture previous for potential undo in future (not shown)
    updateTransaction(payload);
    cancelEdit();
  };

  const sortedTransactions = useMemo(() => {
    const rows = [...filteredTransactions];
    rows.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortBy === "date")
        return (dayjs(a.date).valueOf() - dayjs(b.date).valueOf()) * dir;
      if (sortBy === "title") return a.title.localeCompare(b.title) * dir;
      if (sortBy === "type") return a.type.localeCompare(b.type) * dir;
      if (sortBy === "category")
        return (a.category || "").localeCompare(b.category || "") * dir;
      if (sortBy === "amount") return (a.amount - b.amount) * dir;
      if (sortBy === "currency")
        return (a.currency || "").localeCompare(b.currency || "") * dir;
      return 0;
    });
    return rows;
  }, [filteredTransactions, sortBy, sortDir]);

  const requestDelete = (tx: Transaction) => {
    deleteTransaction(tx.id);
  };

  const onClickSort = (key: typeof sortBy) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir(key === "date" ? "desc" : "asc");
    }
  };

  return (
    <section className="p-5 flex justify-center">
      <div className="container-site">
        <div>
          {/* Header */}
          <div className="flex flex-wrap gap-5 justify-between items-center mb-4">
            <div>
              <h3 className="heading-2">Transactions</h3>
              <p className="sub-heading-2">
                Track, edit, and visualize your activity
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <Input
                id="search"
                type="text"
                placeholder="Search title..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-56"
              />
              <button
                className="btn-ghost p-2"
                onClick={() => setShowModal(true)}
                aria-label="Add transaction"
                title="Add"
              >
                <Plus className="h-4 w-4" />
              </button>
              <button
                className="relative btn-ghost p-2"
                title="Filters"
                aria-label="Open filters"
                onClick={() => setShowFilters(true)}
              >
                <SlidersHorizontal className="h-4 w-4" />
                {useMemo(() => {
                  let count = 0;
                  if (searchText.trim() !== "") count++;
                  if (filterType !== "all") count++;
                  if (selectedCategory !== "all") count++;
                  if (period !== "month") count++;
                  if (period === "range" && (dateFrom || dateTo)) count++;
                  return count > 0 ? (
                    <span className="absolute -top-1 -right-1 min-w-[1.1rem] h-4 px-1 rounded-full bg-indigo-600 text-[10px] leading-4 text-white text-center">
                      {count}
                    </span>
                  ) : null;
                }, [
                  searchText,
                  filterType,
                  selectedCategory,
                  period,
                  dateFrom,
                  dateTo,
                ])}
              </button>
              <button
                className="btn-ghost p-2"
                onClick={exportCSV}
                disabled={filteredTransactions.length === 0}
                title={
                  filteredTransactions.length === 0
                    ? "No rows to export"
                    : "Export filtered rows to CSV"
                }
                aria-label="Export CSV"
              >
                <Download className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Filters Modal (modernized) */}
          <Modal
            isOpen={showFilters}
            onClose={() => setShowFilters(false)}
            title="Filters"
          >
            <form
              className="space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                setShowFilters(false);
              }}
            >
              <div>
                <Label className="mb-2 block">Quick Period</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: "month", label: "This Month" },
                    { id: "7d", label: "Last 7 Days" },
                    { id: "30d", label: "Last 30 Days" },
                    { id: "all", label: "All Time" },
                    { id: "range", label: "Custom Range" },
                  ].map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPeriod(p.id)}
                      className={[
                        "px-3 py-1.5 rounded-md text-sm border",
                        period === p.id
                          ? "bg-indigo-600 text-white border-indigo-500"
                          : "bg-white/5 text-white/90 border-white/15 hover:bg-white/10",
                      ].join(" ")}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search moved to header for seamless typing */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1 block">Type</Label>
                  <SimpleSelect
                    value={filterType}
                    onValueChange={(v) => setFilterType(v)}
                    options={[
                      { label: "All Types", value: "all" },
                      ...transactionTypes.map((t) => ({
                        label: t.name,
                        value: t.id,
                      })),
                    ]}
                  />
                </div>
                <div>
                  <Label className="mb-1 block">Category</Label>
                  <SimpleSelect
                    value={selectedCategory}
                    onValueChange={(v) => setSelectedCategory(v)}
                    options={[
                      { label: "All Categories", value: "all" },
                      ...categories.map((c) => ({
                        label: c.name,
                        value: c.id,
                      })),
                    ]}
                  />
                </div>
              </div>

              {period === "range" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="from" className="mb-1 block">
                      From
                    </Label>
                    <Input
                      id="from"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="to" className="mb-1 block">
                      To
                    </Label>
                    <Input
                      id="to"
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {useMemo(() => {
                if (period === "range" && dateFrom && dateTo) {
                  const invalid = dayjs(dateFrom).isAfter(dayjs(dateTo));
                  return invalid ? (
                    <p className="text-red-400 text-sm">
                      From date cannot be after To date.
                    </p>
                  ) : null;
                }
                return null;
              }, [period, dateFrom, dateTo])}

              <div className="pt-2 flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSearchText("");
                    setFilterType("all");
                    setSelectedCategory("all");
                    setPeriod("month");
                    setDateFrom("");
                    setDateTo("");
                  }}
                >
                  Reset
                </Button>
                <Button type="submit">Apply</Button>
              </div>
            </form>
          </Modal>

          {/* Content views */}
          {filteredTransactions.length === 0 ? (
            <p className="text-slate-500 text-center py-6">
              No matching transactions found.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-white/10">
              <Table className="min-w-full table-fixed">
                <THead>
                  <TR>
                    <TH
                      role="columnheader"
                      aria-sort={
                        sortBy === "date"
                          ? sortDir === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                      onClick={() => onClickSort("date")}
                      className="cursor-pointer select-none"
                    >
                      Date{" "}
                      {sortBy === "date" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                    </TH>
                    <TH
                      role="columnheader"
                      aria-sort={
                        sortBy === "title"
                          ? sortDir === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                      onClick={() => onClickSort("title")}
                      className="cursor-pointer select-none"
                    >
                      Title{" "}
                      {sortBy === "title"
                        ? sortDir === "asc"
                          ? "▲"
                          : "▼"
                        : ""}
                    </TH>

                    <TH
                      role="columnheader"
                      aria-sort={
                        sortBy === "category"
                          ? sortDir === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                      onClick={() => onClickSort("category")}
                      className="cursor-pointer select-none"
                    >
                      Category{" "}
                      {sortBy === "category"
                        ? sortDir === "asc"
                          ? "▲"
                          : "▼"
                        : ""}
                    </TH>
                    <TH
                      role="columnheader"
                      aria-sort={
                        sortBy === "amount"
                          ? sortDir === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                      onClick={() => onClickSort("amount")}
                      className="cursor-pointer select-none"
                    >
                      Amount{" "}
                      {sortBy === "amount"
                        ? sortDir === "asc"
                          ? "▲"
                          : "▼"
                        : ""}
                    </TH>
                    <TH
                      role="columnheader"
                      aria-sort={
                        sortBy === "currency"
                          ? sortDir === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                      onClick={() => onClickSort("currency")}
                      className="cursor-pointer select-none"
                    >
                      Currency{" "}
                      {sortBy === "currency"
                        ? sortDir === "asc"
                          ? "▲"
                          : "▼"
                        : ""}
                    </TH>
                    <TH>Notes</TH>
                    <TH className="text-right w-[100px]">Actions</TH>
                  </TR>
                </THead>
                <TBody>
                  {sortedTransactions.map((tx) => (
                    <TR key={tx.id}>
                      {editingId === tx.id ? (
                        <>
                          <TD>
                            <Input
                              type="datetime-local"
                              className="h-8 py-1"
                              value={dayjs(editRow.date || tx.date).format(
                                "YYYY-MM-DDTHH:mm"
                              )}
                              onChange={(e) =>
                                setEditRow({
                                  ...editRow,
                                  date: e.target.value,
                                })
                              }
                            />
                          </TD>
                          <TD>
                            <Input
                              className="h-8 py-1"
                              value={editRow.title || ""}
                              onChange={(e) =>
                                setEditRow({
                                  ...editRow,
                                  title: e.target.value,
                                })
                              }
                            />
                          </TD>

                          <TD>
                            <SimpleSelect
                              className="h-8"
                              value={editRow.category || tx.category}
                              onValueChange={(v) =>
                                setEditRow({
                                  ...editRow,
                                  category: v,
                                })
                              }
                              options={categories.map((c) => ({
                                label: c.name,
                                value: c.id,
                              }))}
                            />
                          </TD>
                          <TD>
                            <Input
                              type="number"
                              step="0.01"
                              className="h-8 py-1"
                              value={String(editRow.amount ?? tx.amount)}
                              onChange={(e) =>
                                setEditRow({
                                  ...editRow,
                                  amount: Number(e.target.value),
                                })
                              }
                            />
                          </TD>
                          <TD>
                            <Input
                              className="h-8 py-1"
                              value={editRow.currency || tx.currency}
                              onChange={(e) =>
                                setEditRow({
                                  ...editRow,
                                  currency: e.target.value,
                                })
                              }
                            />
                          </TD>
                          <TD>
                            <Input
                              className="h-8 py-1"
                              value={editRow.notes || tx.notes || ""}
                              onChange={(e) =>
                                setEditRow({
                                  ...editRow,
                                  notes: e.target.value,
                                })
                              }
                            />
                          </TD>
                          <TD className="text-right space-x-2 flex pr-5">
                            <Button
                              size="icon"
                              aria-label="Save"
                              title="Save"
                              onClick={saveEdit}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Cancel"
                              title="Cancel"
                              onClick={cancelEdit}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </TD>
                        </>
                      ) : (
                        <>
                          <TD className="text-gray-300">
                            {formatDate(tx.date)}
                          </TD>
                          <TD className="text-white">{tx.title}</TD>

                          <TD className="text-gray-300">
                            {categories.find((c) => c.id === tx.category)
                              ?.name || tx.category}
                          </TD>
                          <TD>
                            <span
                              className={
                                transactionTypes.find((t) => t.id === tx.type)
                                  ?.color || "text-gray-200"
                              }
                            >
                              {tx.amount.toFixed(2)}
                            </span>
                          </TD>
                          <TD className="text-gray-300">{tx.currency}</TD>
                          <TD
                            className="text-gray-300 truncate max-w-[240px]"
                            title={tx.notes || ""}
                          >
                            {tx.notes || ""}
                          </TD>
                          <TD className="text-right space-x-2 flex pr-5">
                            <button
                              className="btn-ghost p-2"
                              aria-label="Edit transaction"
                              title="Edit"
                              onClick={() => startEdit(tx)}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              className="btn-ghost p-2"
                              aria-label="Delete transaction"
                              title="Delete"
                              onClick={() => requestDelete(tx as any)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </TD>
                        </>
                      )}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}

          <Modal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            title="Add Transaction"
          >
            <AddTransactionForm
              onSubmit={handleAddTransaction}
              onError={handleError}
            />
          </Modal>
        </div>
      </div>
    </section>
  );
}
